import React from 'react';
import PropTypes from 'prop-types';
import { SWRConfig, unstable_serialize } from 'swr';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { wrapper } from '@store/store';
import { fetchShopEventProducts } from '@thunks/shopEvent';
import { fetchBasketPaymentMethods } from '@thunks/basket';
import { selectShopEventStatus } from '@selectors/shopEvent';

import ShopEvent from '@routes/ShopEvent';
import Error500 from '@routes/Error500';

import { fetcher } from '@utils/fetchers';
import { getRequestInstance } from '@utils/request';

import { SHOP_EVENT_STATUS } from '@common/constants/shopEvent';
import { shopEventPage, shopEventUserCount } from '@common/endpoints';

const ShopEventPage = ({ pageData, isError, fallback }) =>
  isError ? (
    <Error500 />
  ) : (
    <SWRConfig value={{ fallback }}>
      <ShopEvent pageData={pageData} />
    </SWRConfig>
  );

export const getServerSideProps = wrapper.getServerSideProps(
  store => async context => {
    const { locale } = context;
    const request = getRequestInstance(context);
    const { shopEventStatus } = selectShopEventStatus(store.getState());
    let pageData = {};
    let isError = false;
    let userCounterFallbackValue = { userCount: null };

    try {
      const [, { data }, userCounterData] = await Promise.all([
        store.dispatch(fetchBasketPaymentMethods()),
        request.get(shopEventPage()),
        ...(shopEventStatus === SHOP_EVENT_STATUS.OPEN
          ? [
              fetcher(shopEventUserCount()).catch(
                () => userCounterFallbackValue,
              ),
              store.dispatch(fetchShopEventProducts()),
            ]
          : []),
      ]);

      if (userCounterData) {
        userCounterFallbackValue = userCounterData;
      }

      pageData = data;
    } catch (e) {
      pageData = {};
      isError = true;
    }

    return {
      props: {
        ...(await serverSideTranslations(locale)),
        pageData,
        isError,
        fallback: {
          [unstable_serialize(shopEventUserCount)]: userCounterFallbackValue,
        },
      },
    };
  },
);

ShopEventPage.propTypes = {
  pageData: PropTypes.shape({
    body: PropTypes.arrayOf(PropTypes.shape({})),
    meta: PropTypes.shape({}),
  }).isRequired,
  isError: PropTypes.bool.isRequired,
  fallback: PropTypes.shape({
    userCount: PropTypes.number,
  }),
};

ShopEventPage.defaultProps = {
  fallback: {
    userCount: undefined,
  },
};

export default ShopEventPage;
